/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-05
 */

// Add third-party dependencies.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Fade,
  Zoom,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { Link } from 'react-router-dom';

// Add local dependencies.
import { usePasswordResetRequestMutation } from '../../../features/auth';
import { useFormValidation } from '../../../hooks/useFormValidation';
import { passwordResetRequestSchema } from '../../../utils/validationSchemas';
import { useSuccessNotification } from '../../../hooks/useSuccessNotification';
import SuccessAlertContainer from '../../common/SuccessAlertContainer';
import ErrorAlert from '../../common/ErrorAlert';
import { categorizeError } from '../../../utils/errorHandling';

interface FormData {
  email: string;
}

/**
 * Password reset request form component.
 * Allows users to request a password reset email.
 * @example
 * return <PasswordResetRequestForm />
 * @returns {React.ReactElement} The password reset request form component.
 */
const PasswordResetRequestForm: React.FC = () => {
  const { t, i18n } = useTranslation('auth');
  const [passwordResetRequest, { isLoading }] =
    usePasswordResetRequestMutation();
  const { showPasswordResetEmailSent } = useSuccessNotification();

  const [formData, setFormData] = useState<FormData>({
    email: '',
  });

  const [apiError, setApiError] = useState<string>('');
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldValidationStates, setFieldValidationStates] = useState<{
    email: 'idle' | 'valid' | 'invalid';
  }>({
    email: 'idle',
  });

  // Enhanced Turnstile error handling state
  const [turnstileStatus, setTurnstileStatus] = useState<
    'loading' | 'ready' | 'error' | 'unavailable'
  >('loading');
  const [turnstileError, setTurnstileError] = useState<string>('');
  const [retryAttempts, setRetryAttempts] = useState(0);

  // Initialize form validation with password reset request schema
  const {
    errors,
    validateForm,
    handleInputChange: handleValidationChange,
  } = useFormValidation<FormData>(passwordResetRequestSchema, {
    validateOnBlur: true,
  });

  const widgetIdRef = useRef<string | null>(null);

  const handleTurnstileSuccess = (token: string) => {
    setTurnstileToken(token);
    setTurnstileStatus('ready');
    setTurnstileError('');
  };

  /**
   * Initialize Turnstile widget with enhanced error handling
   */
  const initializeTurnstileWidget = useCallback(() => {
    const container = document.getElementById('turnstile-widget');
    if (!container) {
      setTurnstileStatus('error');
      setTurnstileError('Widget container not found. Please refresh the page.');
      return;
    }

    // Check for missing site key
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey) {
      setTurnstileStatus('unavailable');
      setTurnstileError(
        'Security verification configuration is missing. You can still proceed with your request.'
      );
      return;
    }

    // Remove any existing widget first
    if (widgetIdRef.current !== null && window.turnstile) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (error) {
        // Ignore cleanup errors
      }
    }

    // Clear container content to prevent duplicates
    container.innerHTML = '';

    // Render new widget
    if (window.turnstile) {
      try {
        const newWidgetId = window.turnstile.render(container, {
          sitekey: siteKey,
          callback: handleTurnstileSuccess,
          'error-callback': () => {
            setTurnstileStatus('error');
            setTurnstileError(
              'Security verification failed to load. Please try again.'
            );
          },
        });
        widgetIdRef.current = newWidgetId || null;

        if (newWidgetId) {
          setTurnstileStatus('ready');
        } else {
          setTurnstileStatus('error');
          setTurnstileError(
            'Failed to initialize security verification. Please try again.'
          );
        }
      } catch (error) {
        setTurnstileStatus('error');
        const categorized = categorizeError(error);
        setTurnstileError(
          categorized.message ||
            'Failed to render security verification widget.'
        );
      }
    } else {
      setTurnstileStatus('error');
      setTurnstileError(
        'Security verification service is not available. Please try again.'
      );
    }
  }, []);

  /**
   * Retry Turnstile initialization with exponential backoff
   */
  const retryTurnstileInitialization = () => {
    if (retryAttempts >= 3) {
      setTurnstileStatus('unavailable');
      setTurnstileError(
        'Security verification is temporarily unavailable. You can still proceed with your request.'
      );
      return;
    }

    setRetryAttempts(prev => prev + 1);
    setTurnstileStatus('loading');
    setTurnstileError('');

    // Exponential backoff delay
    const delay = 1000 * 2 ** retryAttempts;
    setTimeout(() => {
      initializeTurnstileWidget();
    }, delay);
  };

  // Initialize Cloudflare Turnstile
  useEffect(() => {
    let mounted = true;

    const loadTurnstileScript = () => {
      // Load Turnstile script if not already loaded
      if (!window.turnstile) {
        // Check if script is already in the DOM
        const existingScript = document.querySelector(
          'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
        );

        if (!existingScript) {
          const script = document.createElement('script');
          script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
          script.async = true;
          script.defer = true;
          script.onload = () => {
            if (mounted) {
              initializeTurnstileWidget();
            }
          };
          script.onerror = () => {
            if (mounted) {
              setTurnstileStatus('error');
              setTurnstileError(
                'Failed to load security verification service. Please check your internet connection.'
              );
            }
          };
          document.head.appendChild(script);
        } else {
          // Script exists but not loaded yet, wait for it
          existingScript.addEventListener('load', () => {
            if (mounted) {
              initializeTurnstileWidget();
            }
          });
          existingScript.addEventListener('error', () => {
            if (mounted) {
              setTurnstileStatus('error');
              setTurnstileError(
                'Failed to load security verification service. Please check your internet connection.'
              );
            }
          });
        }
      } else {
        // Turnstile already loaded, render widget
        initializeTurnstileWidget();
      }
    };

    loadTurnstileScript();

    // Cleanup function
    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (error) {
          // Ignore errors during cleanup
        }
      }
      widgetIdRef.current = null;
    };
  }, [initializeTurnstileWidget]); // Include initializeTurnstileWidget dependency

  // Make Turnstile callback globally available
  useEffect(() => {
    (window as any).handleTurnstileSuccess = handleTurnstileSuccess;
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Use validation hook for error handling
    const updatedFormData = {
      ...formData,
      [name]: value,
    };
    handleValidationChange(name as keyof FormData, value, updatedFormData);

    // Update field validation states for visual feedback
    if (name === 'email') {
      const hasError = errors.email;
      const hasValue = value.trim().length > 0;

      let validationState: 'idle' | 'valid' | 'invalid' = 'idle';
      if (hasError) {
        validationState = 'invalid';
      } else if (hasValue) {
        validationState = 'valid';
      }

      setFieldValidationStates(prev => ({
        ...prev,
        email: validationState,
      }));
    }
  };

  /**
   * Gets the validation icon for a field based on its state.
   */
  const getValidationIcon = (fieldName: 'email') => {
    const state = fieldValidationStates[fieldName];
    const hasError = !!errors[fieldName];

    if (state === 'idle') return null;

    if (hasError) {
      return (
        <Zoom in>
          <Error color="error" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    if (state === 'valid') {
      return (
        <Zoom in>
          <CheckCircle color="success" sx={{ fontSize: 20 }} />
        </Zoom>
      );
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setIsSubmitting(true);

    if (!validateForm(formData)) {
      setIsSubmitting(false);
      return;
    }

    // Allow submission when Turnstile is unavailable (fallback verification)
    const canProceedWithoutTurnstile = turnstileStatus === 'unavailable';

    if (!turnstileToken && !canProceedWithoutTurnstile) {
      if (turnstileStatus === 'loading') {
        setApiError('Please wait for security verification to load.');
      } else if (turnstileStatus === 'error') {
        setApiError(
          'Security verification failed to load. Please try refreshing the page or contact support.'
        );
      } else {
        setApiError('Please complete the security verification.');
      }
      setIsSubmitting(false);
      return;
    }

    try {
      // Prepare request data - include turnstile_token only if available
      const requestData: any = {
        email: formData.email,
        language: i18n.language, // Send current i18next language (en/es/fr)
      };

      if (turnstileToken) {
        requestData.turnstile_token = turnstileToken;
      }

      await passwordResetRequest(requestData).unwrap();

      // Show success notification
      showPasswordResetEmailSent(
        'If an account with this email exists, you will receive password reset instructions.'
      );

      // Clear form
      setFormData({ email: '' });
      setTurnstileToken('');
      setFieldValidationStates({ email: 'idle' });

      // Reset Turnstile widget if available
      if (window.turnstile && turnstileToken) {
        window.turnstile.reset('#turnstile-widget');
      }
    } catch (error: any) {
      if (error.data?.email) {
        setApiError(error.data.email[0]);
      } else if (error.data?.turnstile_token) {
        setApiError('Security verification failed. Please try again.');
        // Reset Turnstile widget
        if (window.turnstile) {
          window.turnstile.reset('#turnstile-widget');
        }
      } else if (error.data?.non_field_errors) {
        setApiError(error.data.non_field_errors[0]);
      } else {
        setApiError('An error occurred. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Loading backdrop */}
      <Backdrop
        sx={{
          color: '#fff',
          zIndex: theme => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
        open={isLoading && isSubmitting}
      >
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary">
          {t('passwordReset.request.loading.sendingLink')}
        </Typography>
      </Backdrop>

      {/* Progress bar */}
      {(isLoading || isSubmitting) && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: theme => theme.zIndex.appBar + 1,
          }}
        />
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('passwordReset.request.title')}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('passwordReset.request.description')}
        </Typography>

        {/* Contextual success messages for password reset */}
        <SuccessAlertContainer filterByType="general" />

        <Fade in={!!apiError}>
          <Box>
            {apiError && (
              <Alert
                severity="error"
                sx={{
                  mb: 2,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem',
                  },
                }}
              >
                {apiError}
              </Alert>
            )}
          </Box>
        </Fade>

        <TextField
          fullWidth
          label={t('passwordReset.request.form.label.email')}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          error={!!errors.email}
          helperText={errors.email}
          disabled={isLoading || isSubmitting}
          required
          autoComplete="email"
          InputProps={{
            endAdornment: getValidationIcon('email'),
          }}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
              '&.Mui-focused fieldset': {
                borderWidth: '2px',
              },
              transition: 'all 0.2s ease-in-out',
            },
          }}
        />

        {/* Cloudflare Turnstile Widget */}
        <Box sx={{ mb: 3 }}>
          {/* Turnstile loading indicator */}
          {turnstileStatus === 'loading' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('passwordReset.request.loading.loadingVerification')}
              </Typography>
            </Box>
          )}

          {/* Turnstile error alert with retry option */}
          {turnstileStatus === 'error' && turnstileError && (
            <ErrorAlert
              error={turnstileError}
              errorType="network"
              onRetry={retryTurnstileInitialization}
              showRetry={retryAttempts < 3}
              retryLabel={t('passwordReset.request.turnstile.retryButton')}
            />
          )}

          {/* Turnstile unavailable fallback message */}
          {turnstileStatus === 'unavailable' && turnstileError && (
            <ErrorAlert
              error={turnstileError}
              errorType="validation"
              showRetry={false}
            />
          )}

          <div id="turnstile-widget" />
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={
            isLoading ||
            isSubmitting ||
            (!turnstileToken && turnstileStatus !== 'unavailable')
          }
          sx={{
            mt: 1,
            mb: 3,
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            boxShadow: 2,
            '&:hover': {
              boxShadow: 4,
              transform: 'translateY(-1px)',
            },
            '&:active': {
              transform: 'translateY(0)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          {isLoading || isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('passwordReset.request.form.submit')
          )}
        </Button>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2">
            {t('passwordReset.request.links.rememberPassword')}{' '}
            <Link
              to="/login"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              {t('passwordReset.request.links.signIn')}
            </Link>
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default PasswordResetRequestForm;
